import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "launchModal", "resultsModal",
    "launchDefenderName", "workerCount", "workerRange",
    "oddsInfiltration", "oddsCombat", "oddsExtraction", "oddsOverall",
    "launchButton",
    "resultsDefenderName", "resultsBadge",
    "phaseLog", "lootSection", "lootLabel", "lootList",
    "damageSection", "damageText",
    "workerStatus", "collectButton"
  ]

  static values = {
    defenderId: Number,
    defenderUsername: String,
    availableWorkers: Number,
    launchUrl: String,
    raidId: Number,
    phaseLog: Array,
    outcome: String,
    resourcesStolen: Object,
    buildingsDamaged: Array,
    workersLostAttacker: Number,
    collectUrl: String
  }

  connect() {
    this.workers = 0
  }

  openLaunch(event) {
    const el = event.currentTarget
    this.defenderIdValue             = parseInt(el.dataset.pvpRaidModalDefenderIdValue)
    this.defenderUsernameValue       = el.dataset.pvpRaidModalDefenderUsernameValue
    this.availableWorkersValue       = parseInt(el.dataset.pvpRaidModalAvailableWorkersValue)
    this.launchUrlValue              = el.dataset.pvpRaidModalLaunchUrlValue

    const config = this._pvpConfig()
    this.workers = config.min_workers

    this.launchDefenderNameTarget.textContent = this.defenderUsernameValue
    this.updateWorkerDisplay(config)
    this.updateOdds(config)

    this.launchModalTarget.classList.remove("hidden")
    this.launchModalTarget.classList.add("flex")
  }

  closeLaunch() {
    this.launchModalTarget.classList.add("hidden")
    this.launchModalTarget.classList.remove("flex")
  }

  incrementWorkers() {
    const config = this._pvpConfig()
    const max = Math.min(config.min_workers + config.max_extra_workers, this.availableWorkersValue)
    if (this.workers < max) {
      this.workers++
      this.updateWorkerDisplay(config)
      this.updateOdds(config)
    }
  }

  decrementWorkers() {
    const config = this._pvpConfig()
    if (this.workers > config.min_workers) {
      this.workers--
      this.updateWorkerDisplay(config)
      this.updateOdds(config)
    }
  }

  updateWorkerDisplay(config) {
    const max = Math.min(config.min_workers + config.max_extra_workers, this.availableWorkersValue)
    this.workerCountTarget.textContent = this.workers
    this.workerRangeTarget.textContent = `(min ${config.min_workers}, max ${max})`
  }

  updateOdds(config) {
    const extraWorkers = Math.max(this.workers - config.min_workers, 0)
    const workerBonus = Math.min(extraWorkers * config.worker_attack_bonus, config.max_worker_attack_bonus)
    // Defense is completely unknown — show base odds with worker bonus only
    const phases = ["infiltration", "combat", "extraction"]
    const odds = {}
    phases.forEach(phase => {
      odds[phase] = Math.min(config.phases[phase].base_success + workerBonus, 100)
    })

    const colorFor = (pct) => pct >= 90 ? "color:#22c55e" : pct >= 70 ? "color:#f59e0b" : "color:#ef4444"

    this.oddsInfiltrationTarget.innerHTML = `<span style="${colorFor(odds.infiltration)}">${odds.infiltration}%+</span>`
    this.oddsCombatTarget.innerHTML       = `<span style="${colorFor(odds.combat)}">${odds.combat}%+</span>`
    this.oddsExtractionTarget.innerHTML   = `<span style="${colorFor(odds.extraction)}">${odds.extraction}%+</span>`

    const overall = Math.round((odds.infiltration / 100) * (odds.combat / 100) * (odds.extraction / 100) * 100)
    this.oddsOverallTarget.innerHTML = `<span style="${colorFor(overall)}">${overall}%+</span>`
  }

  async launch() {
    this.launchButtonTarget.disabled = true
    this.launchButtonTarget.textContent = "Launching..."

    const body = new FormData()
    body.append("defender_id", this.defenderIdValue)
    body.append("workers", this.workers)

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.launchUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "text/vnd.turbo-stream.html" },
      body: body
    })

    if (response.ok) {
      this.closeLaunch()
    } else {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.launchButtonTarget.disabled = false
      this.launchButtonTarget.textContent = "⚔️ Launch Raid"
    }
  }

  openResults(event) {
    const el = event.currentTarget
    this.raidIdValue              = parseInt(el.dataset.pvpRaidModalRaidIdValue)
    this.defenderUsernameValue    = el.dataset.pvpRaidModalDefenderUsernameValue
    this.outcomeValue             = el.dataset.pvpRaidModalOutcomeValue
    this.phaseLogValue            = JSON.parse(el.dataset.pvpRaidModalPhaseLogValue)
    this.resourcesStolenValue     = JSON.parse(el.dataset.pvpRaidModalResourcesStolenValue)
    this.buildingsDamagedValue    = JSON.parse(el.dataset.pvpRaidModalBuildingsDamagedValue)
    this.workersLostAttackerValue = parseInt(el.dataset.pvpRaidModalWorkersLostAttackerValue)
    this.collectUrlValue          = el.dataset.pvpRaidModalCollectUrlValue

    this.resultsDefenderNameTarget.textContent = this.defenderUsernameValue

    // Badge
    const outcome = this.outcomeValue
    if (outcome === "full_success") {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#22c55e">⚔️ FULL SUCCESS</span>'
    } else if (outcome === "infiltration_failure" || outcome === "combat_failure") {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#ef4444">✗ RAID FAILED</span>'
    } else {
      this.resultsBadgeTarget.innerHTML = '<span style="color:#f59e0b">⚠️ PARTIAL SUCCESS</span>'
    }

    // Phase log
    let logHtml = '<div style="border-left:2px solid #374151; padding-left:10px;">'
    this.phaseLogValue.forEach(p => {
      const color    = p.success ? "#22c55e" : "#ef4444"
      const icon     = p.success ? "✓" : "✗"
      const phaseName = p.phase.charAt(0).toUpperCase() + p.phase.slice(1)
      logHtml += `<div class="mb-2">
        <div class="text-xs uppercase tracking-wider" style="color:${color}">Phase — ${phaseName} ${icon}</div>
        <div class="text-xs text-[#d1d5db] italic">"${p.narrative}"</div>
      </div>`
    })
    logHtml += '</div>'
    this.phaseLogTarget.innerHTML = logHtml

    // Loot
    const stolen = this.resourcesStolenValue || {}
    const stolenEntries = Object.entries(stolen).filter(([, v]) => v > 0)
    if (stolenEntries.length > 0) {
      this.lootSectionTarget.classList.remove("hidden")
      this.lootSectionTarget.className = outcome === "full_success"
        ? "bg-green-950/30 border border-green-700/40 rounded-md p-2 mb-2"
        : "bg-[#1a1500] border border-amber-500/30 rounded-md p-2 mb-2"
      this.lootLabelTarget.textContent = outcome === "full_success" ? "Resources Stolen" : "Partial Theft"
      this.lootLabelTarget.style.color = outcome === "full_success" ? "#22c55e" : "#f59e0b"
      this.lootListTarget.textContent = stolenEntries.map(([rt, v]) => `${v} ${rt.replace(/_/g, " ")}`).join(" · ")
    } else {
      this.lootSectionTarget.classList.add("hidden")
    }

    // Building damage
    const damaged = this.buildingsDamagedValue || []
    if (damaged.length > 0) {
      this.damageSectionTarget.classList.remove("hidden")
      this.damageTextTarget.textContent = damaged.map(b => b.building_type.replace(/_/g, " ")).join(", ") + " damaged"
    } else {
      this.damageSectionTarget.classList.add("hidden")
    }

    // Worker status (attacker side)
    const lost = this.workersLostAttackerValue || 0
    if (lost > 0) {
      this.workerStatusTarget.innerHTML = `<span style="color:#ef4444">💀 ${lost} worker${lost > 1 ? 's' : ''} lost in action</span>`
    } else {
      this.workerStatusTarget.textContent = "👷 All workers returned safely"
    }

    // Collect/Dismiss button
    const hasLoot = stolenEntries.length > 0
    const isFailure = outcome === "infiltration_failure" || outcome === "combat_failure"
    if (isFailure && !hasLoot) {
      this.collectButtonTarget.textContent = "Dismiss"
      this.collectButtonTarget.className =
        "w-full bg-[#374151] hover:bg-[#4b5563] text-[#9ca3af] text-sm py-2 rounded-md font-bold"
    } else {
      this.collectButtonTarget.textContent = "Collect Results"
      this.collectButtonTarget.className =
        "w-full bg-amber-700 hover:bg-amber-600 text-white text-sm py-2 rounded-md font-bold"
    }

    this.resultsModalTarget.classList.remove("hidden")
    this.resultsModalTarget.classList.add("flex")
  }

  async collectOrDismiss() {
    this.collectButtonTarget.disabled = true
    this.collectButtonTarget.textContent = "Processing..."

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(this.collectUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": token, "Accept": "text/vnd.turbo-stream.html" }
    })

    if (response.ok) {
      this.resultsModalTarget.classList.add("hidden")
      this.resultsModalTarget.classList.remove("flex")
    } else {
      const html = await response.text()
      Turbo.renderStreamMessage(html)
      this.collectButtonTarget.disabled = false
      this.collectButtonTarget.textContent = "Collect Results"
    }
  }

  _pvpConfig() {
    // Config embedded from server — read from a data attribute on the panel root or use hardcoded values
    // These match config/game/pvp_raids.yml and are safe to expose on the client for odds display
    return {
      min_workers: 2,
      max_extra_workers: 3,
      worker_attack_bonus: 5,
      max_worker_attack_bonus: 15,
      phases: {
        infiltration: { base_success: 80 },
        combat:       { base_success: 70 },
        extraction:   { base_success: 75 }
      }
    }
  }
}
